import { Component, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { PortfolioService } from 'src/app/pages/portfolio/portfolio.service';
import { ApiEndpoints } from 'src/app/ApiEndpoints';
import { MaterialModule } from 'src/app/app.materials';

interface TradeStep {
  closeness: number;
  description: string;
  new_weights: number[];
}

interface NextBestTradeItem {
  id_w: number;
  lambda_value: number;
  target_utility: number;
  current_utility: number;
  trades: {
    step_1: TradeStep | null;
    step_2: TradeStep | null;
    step_3: TradeStep | null;
  };
}

@Component({
  selector: 'app-next-best-trade',
  templateUrl: './next-best-trade.component.html',
  styleUrls: ['./next-best-trade.component.scss'],
  standalone: true,
  imports: [CommonModule, MaterialModule]
})
export class NextBestTradeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sliderVal: number = 0;

  isLoading = false;
  hasData = false;

  currentTrade: NextBestTradeItem | null = null;

  private nbtData: NextBestTradeItem[] = [];
  private portfolioService: PortfolioService;
  private portfolioId: number | null = null;
  private portfolioSub: Subscription;

  readonly steps: Array<{ key: 'step_1' | 'step_2' | 'step_3'; label: string }> = [
    { key: 'step_1', label: '1-Step Trade' },
    { key: 'step_2', label: '2-Step Swap' },
    { key: 'step_3', label: '3-Step Swap' }
  ];

  constructor(private injector: Injector) {
    this.portfolioService = injector.get(PortfolioService);
  }

  ngOnInit(): void {
    this.portfolioSub = this.portfolioService.portfolio$.subscribe(portfolio => {
      if (portfolio?.id) {
        this.portfolioId = portfolio.id;
        this.fetchData();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sliderVal'] && this.nbtData.length > 0) {
      this.updateCurrentTrade();
    }
  }

  ngOnDestroy(): void {
    this.portfolioSub?.unsubscribe();
  }

  fetchData(): void {
    if (!this.portfolioId) return;
    this.isLoading = true;
    this.hasData = false;

    const endpoint = ApiEndpoints.portfolioNextBestTrade.replace(':id', String(this.portfolioId));
    this.portfolioService.getRequest<any>(endpoint)
      .subscribe({
        next: (resp: any) => {
          this.isLoading = false;
          const data: NextBestTradeItem[] = resp?.data ?? resp?.success?.data ?? [];
          if (data.length > 0) {
            this.nbtData = data;
            this.hasData = true;
            this.updateCurrentTrade();
          }
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private updateCurrentTrade(): void {
    // sliderVal is 0-based; id_w is 1-based
    this.currentTrade = this.nbtData.find(item => item.id_w === this.sliderVal + 1)
      ?? this.nbtData[this.sliderVal]
      ?? null;
  }

  getStep(key: 'step_1' | 'step_2' | 'step_3'): TradeStep | null {
    return this.currentTrade?.trades?.[key] ?? null;
  }

  formatCloseness(value: number): string {
    return (value * 100).toFixed(1) + '%';
  }
}
